import { useMemo, useState } from 'react'
import {
  Box, Button, Divider, Stack, Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded'

import { APP_NAME } from '../../lib/branding'
import { useOrgSettings } from '../../hooks/useOrgSettings'

type Cycle = 'monthly' | 'yearly'

const CURRENT_PLAN_ID = 'free'

// Display-only FX rates (anchored on USD), April 2026. Real billing must
// fetch a per-currency price table at checkout — these only exist so a
// rupee/euro org sees a believable headline instead of raw $.
const FX: Record<string, number> = {
  USD: 1.0, EUR: 0.92, GBP: 0.79, INR: 94.25, CAD: 1.39, AUD: 1.55,
  NZD: 1.69, SGD: 1.34, JPY: 154.0, CNY: 7.25, KRW: 1390.0,
  AED: 3.67, SAR: 3.75, CHF: 0.87, HKD: 7.82, MXN: 17.0, BRL: 5.2,
  ZAR: 19.0, TRY: 32.5, PHP: 56.0, IDR: 16000.0, THB: 36.0,
  VND: 25000.0, NGN: 1450.0, EGP: 47.0, PKR: 278.0, BDT: 110.0,
  LKR: 305.0, NOK: 10.5, SEK: 10.55, DKK: 6.85, PLN: 3.95,
  CZK: 23.1, HUF: 360.0,
}

function niceRound(v: number): number {
  if (v < 10) return Math.round(v)
  let step
  if (v < 100) step = 5
  else if (v < 500) step = 10
  else if (v < 5000) step = 50
  else if (v < 50000) step = 100
  else step = 500
  return Math.round(v / step) * step
}

interface Plan {
  id: string
  name: string
  tagline: string
  /** null = custom pricing */
  priceUsd: number | null
  features: string[]
  ctaLabel: string
  icon: React.ElementType
  gradient: [string, string]
  popular?: boolean
}

const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: `Get a feel for ${APP_NAME} with a small team.`,
    priceUsd: 0,
    icon: FlashOnRoundedIcon,
    gradient: ['#64748B', '#94A3B8'],
    features: [
      'Up to 4 team members',
      '1 site, unlimited locations',
      '50 work orders / month',
      'Mobile + web apps',
      'Basic checklists & requests',
    ],
    ctaLabel: 'Stay on Free',
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Run a real maintenance program with the whole crew.',
    priceUsd: 49,
    icon: BoltRoundedIcon,
    gradient: ['#0D9488', '#0EA5E9'],
    popular: true,
    features: [
      'Up to 20 team members',
      'Unlimited sites & work orders',
      'Workflows, SLAs and audit log',
      'Vendor & inventory management',
      'Attachments and signatures',
      'Email + chat support',
    ],
    ctaLabel: 'Upgrade to Pro',
  },
  {
    id: 'ultra',
    name: 'Ultra',
    tagline: 'Scale across plants with deeper analytics and controls.',
    priceUsd: 149,
    icon: WorkspacePremiumRoundedIcon,
    gradient: ['#EA580C', '#F59E0B'],
    features: [
      'Up to 100 team members',
      'Advanced reports & exports',
      'Custom roles & approval chains',
      'Push notifications & priority queues',
      'Public API access',
      'Priority support, 4-hour response',
    ],
    ctaLabel: 'Upgrade to Ultra',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'Built for global operations and regulated environments.',
    priceUsd: null,
    icon: BusinessRoundedIcon,
    gradient: ['#0F172A', '#334155'],
    features: [
      'Unlimited team members',
      'SSO / SAML and SCIM provisioning',
      'Dedicated success manager',
      'Custom SLAs & audit retention',
      'On-premise or private cloud',
      'Onboarding & training included',
    ],
    ctaLabel: 'Contact sales',
  },
]

const FAQ = [
  {
    icon: RestartAltRoundedIcon,
    title: 'Cancel or change anytime',
    body: 'No lock-in. Upgrade, downgrade, or cancel from billing — we prorate everything and your data stays put.',
  },
  {
    icon: CloudSyncRoundedIcon,
    title: 'Mobile + web, one subscription',
    body: 'Every paid seat works across iOS, Android and the browser app. Technicians in the field, managers at a desk — same workspace.',
  },
  {
    icon: LockOutlinedIcon,
    title: 'Your data, your rules',
    body: 'Encrypted in transit and at rest. Export everything to CSV at any time. We never sell your data and never train models on it.',
  },
  {
    icon: HeadsetMicRoundedIcon,
    title: 'Real humans on support',
    body: 'Free plans get email support; Pro adds chat; Ultra and Enterprise get a 4-hour priority response with a named contact.',
  },
] as const

// Top-of-page trust strip — only claims things the product actually does,
// no fabricated ratings or counts.
const TRUST_BADGES = [
  { icon: LockOutlinedIcon, label: 'No credit card to try' },
  { icon: RestartAltRoundedIcon, label: 'Cancel anytime' },
  { icon: CloudSyncRoundedIcon, label: 'iOS · Android · Web' },
  { icon: VerifiedRoundedIcon, label: 'Encrypted end-to-end' },
] as const

// Outcomes shown in the "Why Pro" band after the plan grid — these
// reframe Pro's feature list as concrete benefits, which converts
// better than a feature dump.
const PRO_OUTCOMES = [
  {
    icon: TrendingUpRoundedIcon,
    title: 'Unlimited work orders',
    body: 'Free caps you at 50/mo. On Pro your team can throw their full workload at the system without rationing.',
  },
  {
    icon: VerifiedRoundedIcon,
    title: 'Compliance-ready',
    body: 'Audit log, SLA policies, signatures and approval chains — everything an inspector or auditor will ever ask for.',
  },
  {
    icon: HeadsetMicRoundedIcon,
    title: 'Priority chat support',
    body: 'Skip the email queue. Real humans, fast replies, while you onboard the rest of your crew.',
  },
] as const

function mailLink(plan: Plan, cycle: Cycle): string {
  const subject = plan.id === 'enterprise'
    ? `${APP_NAME} Enterprise inquiry`
    : `${APP_NAME} ${plan.name} plan — sign me up`
  const body = plan.id === 'enterprise'
    ? `Hi,\n\nI'd like to learn more about the ${APP_NAME} Enterprise plan.\n\nThanks.`
    : `Hi,\n\nI'd like to upgrade my workspace to the ${plan.name} plan (${cycle} billing).\n\nThanks.`
  return `mailto:sales@bacsys.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

export function SubscriptionTab() {
  const [cycle, setCycle] = useState<Cycle>('monthly')
  const { prefs, formatCurrency } = useOrgSettings()

  const priced = useMemo(() => (usd: number | null): {
    label: string
    unit: string | null
    /** Strikethrough price shown on yearly toggle so the discount is visible. */
    wasLabel: string | null
    /** Annual savings vs monthly billing (only set on yearly). */
    savesLabel: string | null
  } => {
    if (usd === null) return { label: 'Custom', unit: null, wasLabel: null, savesLabel: null }
    if (usd === 0) return { label: formatCurrency(0), unit: '/mo', wasLabel: null, savesLabel: null }
    const rate = FX[(prefs.currency ?? 'USD').toUpperCase()] ?? 1
    const monthlyUsd = cycle === 'monthly' ? usd : usd * 0.8
    const local = niceRound(monthlyUsd * rate)
    if (cycle === 'monthly') {
      return { label: formatCurrency(local), unit: '/mo', wasLabel: null, savesLabel: null }
    }
    // Yearly: anchor against the monthly price so the saving is visible.
    const monthlyLocal = niceRound(usd * rate)
    const annualSaving = niceRound((usd - usd * 0.8) * 12 * rate)
    return {
      label: formatCurrency(local),
      unit: '/mo, billed yearly',
      wasLabel: formatCurrency(monthlyLocal),
      savesLabel: `Save ${formatCurrency(annualSaving)} / year`,
    }
  }, [cycle, prefs.currency, formatCurrency])

  return (
    // ── Coming Soon shell ───────────────────────────────────────────
    // Billing pipeline isn't wired to a real payment provider yet, so
    // the entire subscription UI sits behind a coming-soon overlay
    // mirroring the Help & Support pattern — single centred card with
    // a blurred preview of the real content behind it. Drop the
    // overlay block once Stripe / Razorpay is hooked up.
    <Box sx={{ position: 'relative', minHeight: '60vh' }}>
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
      }}>
        <Box sx={{
          px: 6, py: 5, borderRadius: 3.5, textAlign: 'center',
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          boxShadow: '0 12px 40px -10px rgba(0,0,0,0.2)',
          maxWidth: 440,
        }}>
          <BoltRoundedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>Coming Soon</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.7 }}>
            Self-serve plans and billing land here next. For now, every workspace ships on
            the full feature set.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 2.5 }}>
            For now, email sales@bacsys.ai for procurement or seat-limit questions.
          </Typography>
        </Box>
      </Box>

      {/* Blurred preview content behind */}
      <Box sx={{ pointerEvents: 'none', userSelect: 'none', filter: 'blur(3px)' }}>
    <Box sx={{ pb: 6, maxWidth: 1100, mx: 'auto' }}>
      {/* ── Hero ───────────────────────────────────────────────── */}
      <Stack spacing={1.75} sx={{ mb: 3, alignItems: 'center', textAlign: 'center' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center', gap: 0.75,
            px: 1.25, py: 0.5, borderRadius: 99,
            bgcolor: (t) => t.palette.mode === 'light'
              ? 'rgba(13,148,136,0.10)' : 'rgba(13,148,136,0.18)',
          }}
        >
          <WorkspacePremiumRoundedIcon sx={{ fontSize: 13, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.4, color: 'primary.main' }}>
            Plans & Pricing
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: { xs: 30, md: 42 },
            fontWeight: 900,
            letterSpacing: '-0.035em',
            lineHeight: 1.02,
            background: 'linear-gradient(135deg, #0D9488 0%, #0EA5E9 55%, #6366F1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            maxWidth: 720,
          }}
        >
          Pricing that grows with<br />your operation.
        </Typography>
        <Typography sx={{ fontSize: 15, color: 'text.secondary', maxWidth: 580, lineHeight: 1.55 }}>
          Start free, upgrade when your team is ready. Every plan includes the
          same fast mobile and web apps — pay only for the depth you actually use.
        </Typography>

        {/* Trust strip — real product facts only, no fabricated ratings */}
        <Stack
          direction="row" spacing={2.5} flexWrap="wrap" useFlexGap
          justifyContent="center"
          sx={{ pt: 1, rowGap: 1 }}
        >
          {TRUST_BADGES.map(({ icon: I, label }) => (
            <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
              <I sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}>
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* ── Cycle toggle ───────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          exclusive
          value={cycle}
          onChange={(_, v) => v && setCycle(v)}
          sx={{
            p: 0.5, borderRadius: 99,
            bgcolor: (t) => t.palette.mode === 'light' ? '#F4F4F6' : 'rgba(255,255,255,0.04)',
            border: '1px solid', borderColor: 'divider',
            '& .MuiToggleButton-root': {
              border: 'none', borderRadius: '99px !important',
              textTransform: 'none', fontSize: 13, fontWeight: 700,
              px: 2.5, py: 0.85,
              color: 'text.secondary',
              '&.Mui-selected': {
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: 'background.paper' },
              },
            },
          }}
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="yearly">
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <span>Yearly</span>
              <Box sx={{
                px: 0.75, py: 0.25, borderRadius: 99, fontSize: 9.5, fontWeight: 800,
                letterSpacing: 0.2,
                bgcolor: cycle === 'yearly' ? 'success.main' : 'transparent',
                color: cycle === 'yearly' ? '#fff' : 'text.disabled',
                border: cycle === 'yearly' ? 'none' : '1px solid',
                borderColor: 'divider',
              }}>
                Save 20%
              </Box>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Plan grid ──────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gap: 2.25,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          alignItems: 'stretch',
        }}
      >
        {PLANS.map((plan) => {
          const isCurrent = plan.id === CURRENT_PLAN_ID
          const Icon = plan.icon
          const { label, unit, wasLabel, savesLabel } = priced(plan.priceUsd)
          const card = (
            <Box
              sx={{
                position: 'relative',
                display: 'flex', flexDirection: 'column',
                p: 2.75,
                borderRadius: plan.popular ? '18px' : '20px',
                bgcolor: 'background.paper',
                border: plan.popular ? 'none' : '1px solid',
                borderColor: 'divider',
                height: '100%',
                transition: 'transform .18s ease, box-shadow .18s ease',
                '&:hover': plan.popular ? {} : {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 28px -16px rgba(15,23,42,0.18)',
                  borderColor: (t) => t.palette.mode === 'light'
                    ? 'rgba(15,23,42,0.16)' : 'rgba(255,255,255,0.18)',
                },
              }}
            >
              {/* Header: icon + name + pill */}
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `linear-gradient(135deg, ${plan.gradient[0]}, ${plan.gradient[1]})`,
                  boxShadow: `0 4px 10px ${plan.gradient[0]}40`,
                }}>
                  <Icon sx={{ fontSize: 18, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {plan.name}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {plan.popular && !isCurrent && (
                  <Box sx={{
                    px: 1, py: 0.4, borderRadius: 99,
                    background: `linear-gradient(135deg, ${plan.gradient[0]}, ${plan.gradient[1]})`,
                    color: '#fff',
                  }}>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: '#fff' }}>
                      Most popular
                    </Typography>
                  </Box>
                )}
                {isCurrent && (
                  <Box sx={{
                    px: 1, py: 0.4, borderRadius: 99,
                    bgcolor: (t) => t.palette.mode === 'light'
                      ? 'rgba(13,148,136,0.12)' : 'rgba(13,148,136,0.22)',
                  }}>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: 'primary.main' }}>
                      Current
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Price — with strikethrough monthly anchor + savings line on yearly */}
              <Stack direction="row" alignItems="flex-end" spacing={0.75} sx={{ mb: 0.5 }}>
                {wasLabel && (
                  <Typography sx={{
                    fontSize: 16, fontWeight: 700, color: 'text.disabled',
                    textDecoration: 'line-through', mb: 0.5,
                  }}>
                    {wasLabel}
                  </Typography>
                )}
                <Typography sx={{
                  fontSize: 32, fontWeight: 900, letterSpacing: '-0.04em',
                  lineHeight: 1, color: 'text.primary',
                }}>
                  {label}
                </Typography>
                {unit && (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                    {unit}
                  </Typography>
                )}
              </Stack>
              {savesLabel ? (
                <Typography sx={{
                  fontSize: 11.5, fontWeight: 800, letterSpacing: 0.2,
                  color: 'success.main', mb: 1.25,
                }}>
                  {savesLabel}
                </Typography>
              ) : (
                <Box sx={{ height: 18, mb: 0.25 }} />
              )}
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5, mb: 2, minHeight: 36 }}>
                {plan.tagline}
              </Typography>

              {/* Features */}
              <Stack spacing={1} sx={{ flex: 1, mb: 2.5 }}>
                {plan.features.map((f) => (
                  <Stack key={f} direction="row" spacing={1.25} alignItems="flex-start">
                    <Box sx={{
                      mt: '2px', width: 18, height: 18, borderRadius: 99,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${plan.gradient[0]}22`,
                    }}>
                      <CheckRoundedIcon sx={{ fontSize: 12, color: plan.gradient[0] }} />
                    </Box>
                    <Typography sx={{ fontSize: 12.5, lineHeight: 1.45, fontWeight: 500 }}>
                      {f}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {/* CTA */}
              <Button
                fullWidth
                disabled={isCurrent}
                href={isCurrent ? undefined : mailLink(plan, cycle)}
                sx={{
                  height: 46, fontSize: 13.5, fontWeight: 800, letterSpacing: 0.1,
                  borderRadius: 1.75, textTransform: 'none',
                  background: plan.popular
                    ? `linear-gradient(135deg, ${plan.gradient[0]}, ${plan.gradient[1]})`
                    : plan.gradient[0],
                  color: '#fff',
                  border: 'none',
                  boxShadow: plan.popular ? `0 8px 18px -6px ${plan.gradient[0]}88` : 'none',
                  '&:hover': {
                    background: plan.popular
                      ? `linear-gradient(135deg, ${plan.gradient[1]}, ${plan.gradient[0]})`
                      : plan.gradient[1],
                    boxShadow: plan.popular
                      ? `0 10px 22px -6px ${plan.gradient[0]}aa`
                      : `0 6px 14px -6px ${plan.gradient[0]}66`,
                  },
                  '&.Mui-disabled': {
                    bgcolor: (t) => t.palette.mode === 'light' ? '#F4F4F6' : 'rgba(255,255,255,0.06)',
                    color: 'text.disabled',
                    background: 'none',
                  },
                }}
              >
                {isCurrent ? 'Current plan' : plan.ctaLabel}
              </Button>
            </Box>
          )

          if (!plan.popular) return <Box key={plan.id}>{card}</Box>

          // Highlighted card — gradient ring + lift to draw the eye.
          return (
            <Box
              key={plan.id}
              sx={{
                p: '2px',
                borderRadius: '20px',
                background: `linear-gradient(135deg, ${plan.gradient[0]}, ${plan.gradient[1]})`,
                boxShadow: `0 18px 40px -12px ${plan.gradient[0]}66`,
                transform: { lg: 'translateY(-8px)' },
                transition: 'transform .25s ease, box-shadow .25s ease',
                '&:hover': {
                  transform: { xs: 'none', lg: 'translateY(-12px)' },
                  boxShadow: `0 22px 48px -12px ${plan.gradient[0]}88`,
                },
              }}
            >
              {card}
            </Box>
          )
        })}
      </Box>

      {/* ── Why Pro outcomes band ──────────────────────────────── */}
      <Box sx={{ mt: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #0D9488, #0EA5E9)',
            color: '#fff', boxShadow: '0 4px 10px rgba(13,148,136,0.35)',
          }}>
            <TrendingUpRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>
            What changes when you upgrade to Pro
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gap: 1.75,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          }}
        >
          {PRO_OUTCOMES.map(({ icon: I, title, body }) => (
            <Box
              key={title}
              sx={{
                p: 2.25, borderRadius: 2.5,
                border: '1px solid', borderColor: 'divider',
                bgcolor: (t) => t.palette.mode === 'light'
                  ? 'rgba(13,148,136,0.04)' : 'rgba(13,148,136,0.08)',
              }}
            >
              <Box sx={{
                width: 30, height: 30, borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'rgba(13,148,136,0.14)', color: 'primary.main', mb: 1.25,
              }}>
                <I sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 800, mb: 0.5 }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.55 }}>
                {body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── FAQ / objections ───────────────────────────────────── */}
      <Box sx={{ mt: 5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', mb: 2 }}>
          Common questions, before you commit
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          }}
        >
          {FAQ.map(({ icon: I, title, body }) => (
            <Stack
              key={title}
              direction="row" spacing={1.5} alignItems="flex-start"
              sx={{
                p: 2, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.25, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (t) => t.palette.mode === 'light'
                  ? 'rgba(13,148,136,0.10)' : 'rgba(13,148,136,0.20)',
              }}>
                <I sx={{ fontSize: 16, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 0.5 }}>{title}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.55 }}>
                  {body}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Box>

      <Divider sx={{ mt: 4, mb: 3, borderStyle: 'dashed' }} />

      {/* ── Footnote ───────────────────────────────────────────── */}
      <Typography sx={{
        mt: 3, fontSize: 11, color: 'text.disabled', lineHeight: 1.55, fontWeight: 500,
      }}>
        Prices shown in {(prefs.currency ?? 'USD').toUpperCase()} to match your workspace settings.
        Taxes may apply at checkout. Email{' '}
        <Box component="a" href="mailto:sales@bacsys.ai" sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 700 }}>
          sales@bacsys.ai
        </Box>{' '}
        for procurement, security questionnaires, or billing questions.
      </Typography>
    </Box>
      </Box>
    </Box>
  )
}
